import React from 'react';
import {
  Button as MuiButton,
  CircularProgress,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { designTokens } from '../../../design-system/tokens';

/**
 * SolidButton Props
 */
export interface SolidButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  /** 로딩 상태 */
  loading?: boolean;
  /** 버튼 내용 */
  children: React.ReactNode;
  /** 테스트 ID */
  testId?: string;
}

/**
 * Dream Logistics Solid Button 컴포넌트
 *
 * Dream Dark (#003F5F) 색상을 사용하는 고정 스타일 버튼
 *
 * 스펙:
 * - 최소 너비: 80px
 * - 높이: 30px
 * - 패딩: 0 27px
 * - 배경색: #003F5F (Dream Dark)
 * - Border Radius: 4px
 *
 * @component
 * @example
 * // 기본 사용
 * <SolidButton onClick={handleClick}>
 *   저장
 * </SolidButton>
 *
 * @example
 * // 로딩 상태
 * <SolidButton loading={isSubmitting} onClick={handleSubmit}>
 *   제출
 * </SolidButton>
 *
 * @example
 * // 아이콘과 함께 사용
 * <SolidButton startIcon={<SaveIcon />} onClick={handleSave}>
 *   저장
 * </SolidButton>
 */
export const SolidButton: React.FC<SolidButtonProps> = ({
  children,
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  onClick,
  type = 'button',
  className,
  testId,
  sx,
  ...props
}) => {
  const isDisabled = disabled || loading;

  return (
    <MuiButton
      variant="contained"
      disabled={isDisabled}
      onClick={onClick}
      type={type}
      className={className}
      data-testid={testId}
      startIcon={loading ? undefined : startIcon}
      endIcon={loading ? undefined : endIcon}
      sx={{
        // Layout
        display: 'flex',
        minWidth: '80px',
        height: '30px',
        padding: '0 27px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',

        // Style
        borderRadius: '4px',
        backgroundColor: designTokens.colors.brand.dreamDark,
        color: '#ffffff',

        // Typography
        fontSize: designTokens.typography.fontSize.sm,
        fontWeight: designTokens.typography.fontWeight.medium,
        textTransform: 'none',

        // Interaction
        boxShadow: 'none',
        transition: `all ${designTokens.animations.duration.normal} ${designTokens.animations.easing.easeInOut}`,

        '&:hover': {
          backgroundColor: '#005A7F', // dreamDark.light
          boxShadow: designTokens.shadows.sm,
          transform: 'translateY(-1px)',
        },

        '&:active': {
          backgroundColor: '#002940', // dreamDark.dark
          transform: 'translateY(0)',
        },

        '&.Mui-disabled': {
          backgroundColor: '#BDBDBD',
          color: '#FFFFFF',
          opacity: 0.6,
        },

        // 사용자 정의 sx 속성 병합
        ...sx,
      }}
      {...props}
    >
      {loading && <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />}
      {children}
    </MuiButton>
  );
};

export default SolidButton;
